import { npiRunChannel, parseNpiRunEvent } from '../model/runEvents';
import type { NpiRunChannel } from '../ports/npiRunChannel';

import { useRealtimeChannel } from '@/hooks/realtime/useRealtimeChannel';
import { getIdToken } from '@/services/auth';
import { parseIdTokenClaims } from '@/services/session';

const MONITORING = {
  breadcrumbCategory: 'npi-suggestions.realtime',
  refusedMessage: 'NPI suggestion realtime subscription refused',
  component: 'NpiSuggestionsRealtime',
  source: 'npi-suggestions-realtime',
};

async function requireIdToken(): Promise<string> {
  const token = await getIdToken();

  if (!token) {
    throw new Error('No id token for the realtime connection');
  }

  return token;
}

/**
 * The run's `/npi/{sub}/{runId}` channel on the realtime Event API. `{sub}`
 * comes from the id token the socket authenticates with, because the server
 * admits only the token's own subject; that is also what lets a pending
 * Google signup, with a token and no user row, follow its run.
 */
export const realtimeNpiRunChannel: NpiRunChannel = {
  useRunEvents({ runId, onEvent, onResume, onConnectionChange }) {
    useRealtimeChannel({
      enabled: runId !== null,
      identity: runId ?? undefined,
      getToken: requireIdToken,
      channelFor: (token) => {
        const sub = parseIdTokenClaims(token)?.sub;

        return sub && runId ? npiRunChannel(sub, runId) : null;
      },
      parse: parseNpiRunEvent,
      onEvent,
      onResume,
      onConnectionChange,
      monitoring: MONITORING,
    });
  },
};
